// Repo money-rounding convention: round-half-up to 2 decimals, keeps binary-float noise out of
// ledger amounts. Canonical home for money math shared between the cash-wallet backend and its
// frontend previews — kept dependency-free so bundlers only pull it in via a direct
// `@arbitrip/utils/money` subpath require, not the heavier `core.js` barrel.
function round2(x) {
    return Math.round(x * 100) / 100;
}

// Validated on the RAW value (string or number) so float-representation noise can never widen the
// precision gate and `'-5'`, `'1e3'`, `'abc'`, `''` are all rejected outright. One pattern for every
// money-INPUT surface (withdrawal submission, admin opening balance) so they cannot drift apart.
const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;

function hasAtMostTwoDecimals(raw) {
    if (raw === null || raw === undefined) {
        return false;
    }
    return AMOUNT_RE.test(String(raw).trim());
}

// The agent submits a NET figure; `vat` is derived from it at `vatRate` regardless of classification
// — the wallet carries that VAT liability from the original commission accrual either way. `amount` is
// the agent's PAYOUT: gross (net + vat) when `isAuthorised`, net-only otherwise. Callers decide whether
// a classification is valid/selected at all — this function always computes real numbers.
function deriveNetVat(netAmount, vatRate, isAuthorised) {
    const net = netAmount;
    const vat = round2(net * vatRate);
    return { net, vat, amount: isAuthorised ? round2(net + vat) : net };
}

// The stored wallet balance is GROSS (VAT-inclusive); this converts it to the NET value the agent is
// shown. Never depends on tax classification.
//
// ⚠ DISPLAY ONLY — never use as a SPENDING CAP: it is not the inverse of the debit a withdrawal actually
// posts. Use `maxWithdrawableNet` below.
function deriveNetBalance(balance, vatRate) {
    return round2(balance / (1 + vatRate));
}

/**
 * The largest NET amount whose reconstructed debit still fits inside `balance` — the true inverse of the
 * debit the ledger will post.
 *
 * WHY `deriveNetBalance` is NOT a safe cap: two INDEPENDENT half-up roundings sit on either side of the
 * flow. It rounds once dividing the gross balance down (`round2(balance / (1 + vatRate))`); the debit on
 * Paid rounds twice on the way back up (`round2(net + round2(net * vatRate))`, see
 * packages/cash-wallet/index.js `payWithdrawal`). They do not compose: sweeping every gross USD balance
 * from 500.00 to 2000.00 at 1-cent steps, 12,188 of 150,001 (8.1%) reconstruct to a debit exactly 0.01
 * ABOVE the balance they were derived from — e.g. 500.09 → net 423.81 → vat 76.29 → debit 500.10.
 *
 * That cent is not cosmetic: such a request PASSES submission, reaches Finance, then fails
 * `appendLedgerEntry`'s `balance >= amount` guard and is left STRANDED in `In Review` — the agent can no
 * longer cancel it (cancel CAS-es on `Submitted` only) and Finance's only exit is a Reject whose reason
 * codes do not describe what happened. And it is the "withdraw everything" path the UI actively invites.
 *
 * So the cap is defined by the debit, not by the division: start from the display conversion and walk down
 * in whole cents until the reconstruction fits. Verified to overshoot 0 times across 500,001 balances
 * (0.00–5000.00) at vatRate 0.18 / 0.17 / 0, terminating in at most ONE iteration. A NEGATIVE balance
 * yields a negative cap, so every positive net is rejected.
 */
function maxWithdrawableNet(balance, vatRate) {
    let cap = deriveNetBalance(balance, vatRate);
    while (cap > 0 && round2(cap + round2(cap * vatRate)) > balance) {
        cap = round2(cap - 0.01);
    }
    return cap;
}

module.exports = {
    round2,
    deriveNetVat,
    deriveNetBalance,
    maxWithdrawableNet,
    hasAtMostTwoDecimals,
    AMOUNT_RE,
};
